import React, { useEffect } from 'react';
import chunk from 'lodash/chunk';
import styled, { StylesProps } from '../../../lib/styles';
import { Article } from '../../../models/article';
import Ad, { Ads } from '../../../components/presentational/Ad';
import Typography from '../../../components/presentational/Typography';
import Title from '../Title';
import MainImage from '../MainImage';
import Video from '../Video';
import Spacer from '../../../components/presentational/Spacer';
import Slider from '../../../components/presentational/Slider';
import ProviderCTA from '../ProviderCTA';
import AdhesionAd from '../AdhesionAd';
import ResponsiveRender from '../../../behaviour/ResponsiveRender';
import BlockUpdates from '../../../behaviour/BlockUpdates';
import { trackEvent } from '../../../lib/ga';
import theme from '../../../lib/theme';

import sponsoredArticleLayoutStyles from './SponsoredArticleLayout_styles';
import { raiseEvent, SPONSORED_ARTICLE_VIEW } from '../../../lib/gtm';

const { layoutWidths } = theme;
export interface SponsoredArticleLayoutProps {
  ads: Ads;
  article: Article;
  hideComments?: boolean;
  hideAds?: boolean;
  showLinkbyPubFeed?: boolean;
}

const SPLIT_THRESHOLD = 12;
const SponsoredArticleLayout = ({
  ads,
  //  @ts-ignore
  hideComments,
  hideAds,
  article: {
    id,
    //  @ts-ignore
    article_question,
    sponsored_brand,
    title,
    //  @ts-ignore
    link,
    author,
    category,
    created,
    youtube_video,
    large_image,
    image_caption,
    content,
    gallery,
    slug,
    provider,
  },
  showLinkbyPubFeed,
  styles,
}: SponsoredArticleLayoutProps &
  StylesProps<typeof sponsoredArticleLayoutStyles>) => {

  useEffect(() => {
    // console.log('SponsoredArticleLayout', { ads });
    raiseEvent({
      ...SPONSORED_ARTICLE_VIEW,
      ...{
        sasEventType: `SPONSORED_ARTICLE_VIEW: ${category}`,
      },
    });
  }, []);

  const halves = chunk(
    content,
    content.length >= SPLIT_THRESHOLD
      ? Math.ceil(content.length / 2)
      : content.length
  );

  // console.log(
  //   ads,
  //   hideComments,
  //   hideAds,

  //   article_question,
  //   sponsored_brand,
  //   title,
  //   link,
  //   author,
  //   category,
  //   created,
  //   youtube_video,
  //   large_image,
  //   image_caption,
  //   content,
  //   gallery,
  //   slug,
  //   provider,

  //   styles
  // );

  return (
    <Spacer key={id} space={hideAds ? 's' : 'm'} maxWidth={layoutWidths.contentPane} align={'center'}
      className={`sponsored-layout`}
    >
      {halves.map((pars: string[], i: number) => {
        const isFirstHalf = i === 0;
        const isSecondHalf = i === halves.length - 1;
        const inarticleAd = ads[`sponsored_inarticle${i + 1}`];
        const stickyAd = ads[`sponsored_siderail${i + 1}`];
        const halvedPars = chunk(pars, 2);

        return (
          <Spacer key={i} width={layoutWidths.contentPane} space={'m'}>
            {!hideAds && !isFirstHalf && isSecondHalf && ads && ads.sponsored_inbetween && (
              <Ad ad={ads.sponsored_inbetween} lazy initialSize={[300, 90]} gutter={'s'} />
            )}

            <Spacer relative row space={'s'} maxWidth={layoutWidths.contentPane} align={'stretch'}>
              <Spacer 
                flex={'auto'} 
                maxWidth={'100%'} 
                space={'l'}
              >
                <Spacer space={'s'}>
                  {isFirstHalf && (
                    <Spacer space={'s'}>
                      <Title
                        title={title}
                        author={author}
                        sponsored={true}
                        sponsored_brand={sponsored_brand}
                        category={category}
                        created={created}
                      />

                      {!!youtube_video && (
                        <Video
                          video_id={youtube_video}
                          bg_url={large_image}
                          caption={image_caption}
                        />
                      )}

                      {!!(gallery && gallery.length) && !youtube_video && (
                        <Slider
                          brand={'sas'}
                          images={gallery}
                          aspectRatio={9 / 16}
                          minWidth={300}
                          maxWidth={751}
                          onPreviousSlide={() =>
                            trackEvent({
                              action: 'PREVIOUS SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                          onNextSlide={() =>
                            trackEvent({
                              action: 'NEXT SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                          onGotoSlide={() =>
                            trackEvent({
                              action: 'GOTO SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                        />
                      )}

                      {!!large_image &&
                        !youtube_video &&
                        !(gallery && gallery.length) && (
                          <MainImage
                            bg_url={large_image}
                            caption={image_caption}
                          />
                        )}
                    </Spacer>
                  )}

                  <Spacer key={i} space={'s'}>
                    {halvedPars.map((pars: string[], j: number) => (
                      <React.Fragment key={j}>
                        {isFirstHalf &&
                          j === 1 &&
                          !hideAds && 
                          <Ad ad={inarticleAd} lazy initialSize={[300, 90]} predefined />
                        }

                        <BlockUpdates
                          block
                          render={() => (
                            <Typography
                              dangerouslySetInnerHTML={{
                                __html: pars.join('\n'),
                              }}
                              size={'l'}
                              brand={'sas'}
                              family={'copy'}
                            />
                          )}
                        />
                      </React.Fragment>
                    ))}
                  </Spacer>
                </Spacer>

                {isSecondHalf && provider && (
                  <Spacer space={['l', 'xl']}>
                    {provider && provider.cta_text && (
                      <Spacer width={851}>
                        <ProviderCTA {...provider} brand={'sas'} />
                      </Spacer>
                    )}
                  </Spacer>
                )}

                {!hideAds && isSecondHalf && !isFirstHalf && inarticleAd && (
                  <Ad ad={inarticleAd} lazy initialSize={[300, 90]} predefined />
                )}

                {isSecondHalf && (
                  <>
                    {showLinkbyPubFeed && (
                      <Spacer width={1160} space={'s'}>
                        <Spacer relative row space={'m'} align={'stretch'}>
                          <Spacer flex={'auto'} width={'100%'} space={'m'}>
                            <div className="linkby-widget" data-type="listicle"></div>
                          </Spacer>
                        </Spacer>
                      </Spacer>
                    )}
                  </>
                )}
              </Spacer>

              <Spacer className={styles.lOnly} flex={`0 0 ${layoutWidths.sidebar}px`} relative align={'center'}>
                {!hideAds && stickyAd && (
                  <Ad
                    ad={stickyAd}
                    sticky
                    lazy
                    align={'flex-end'}
                    backgroundColor={'transparent'}
                    initialSize={[120, 600]}
                  />
                )}
              </Spacer>
            </Spacer>
          </Spacer>
        );
      })}

      {!hideAds && ads && ads.sponsored_adhesion && <AdhesionAd ad={ads.sponsored_adhesion} />}
    </Spacer>
  );
};

export default styled(sponsoredArticleLayoutStyles)(SponsoredArticleLayout);
